/**
 * DAKE Tiered Supply Bundles widget.
 *
 * State: adults (1|2), selected tier index, purchase type (subscription | onetime).
 *
 * - Displayed first-order totals come from config.pricing_by_bottles (exact
 *   −20/25/30% dollar amounts keyed by bottle count).
 * - Ongoing price = the Stay AI 10% selling plan on the line item; first-order
 *   price is delivered by fixed-amount codes (DAKES1–4 by bottle count),
 *   auto-applied via Shopify's /discount/CODE endpoint on subscription ATC.
 * - Tier supply months differ by adults (months / months_2adults). Default
 *   selection and the highlight badge follow SUPPLY MONTHS (config.default_months
 *   / config.highlight_months), so both land on the 4-month supply for either
 *   adult count. Toggling adults keeps the currently selected supply length
 *   when the new ladder has it.
 * - Details (cadence, renewal, perks) render as an accordion inside each tier
 *   card, expanded only for the selected tier.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    var value = cents / 100;
    var formatted = value.toFixed(2);
    if (formatted.slice(-3) === '.00') formatted = formatted.slice(0, -3);
    return '$' + formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function planMonths(planName) {
    var match = /every\s+(\d+)\s+month/i.exec(planName || '');
    return match ? parseInt(match[1], 10) : null;
  }

  function initWidget(root) {
    var configEl = root.querySelector('[data-dsb-config]');
    if (!configEl) return;
    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (err) {
      return;
    }

    var tierEls = Array.prototype.slice.call(root.querySelectorAll('[data-dsb-tier]'));
    var adultsEls = Array.prototype.slice.call(root.querySelectorAll('[data-dsb-adults]'));
    var onetimeEl = root.querySelector('[data-dsb-onetime]');
    var onetimeInput = onetimeEl ? onetimeEl.querySelector('input') : null;
    var onetimePriceEl = root.querySelector('[data-dsb-onetime-price]');
    var otQtyEl = root.querySelector('[data-dsb-ot-qty]');
    var otSubEl = root.querySelector('[data-dsb-ot-sub]');
    var otMinusBtn = root.querySelector('[data-dsb-ot-minus]');
    var otPlusBtn = root.querySelector('[data-dsb-ot-plus]');
    var atcBtn = root.querySelector('[data-dsb-atc]');

    function monthsForTier(tier, adults) {
      return adults === 2 ? (tier.months_2adults || tier.months) : tier.months;
    }

    function tierIndexByMonths(months, adults) {
      for (var i = 0; i < config.tiers.length; i++) {
        if (monthsForTier(config.tiers[i], adults) === months) return i;
      }
      return -1;
    }

    var defaultIndex = tierIndexByMonths(config.default_months, 1);
    var state = {
      adults: 1,
      tierIndex: defaultIndex >= 0 ? defaultIndex : 1,
      purchaseType: 'subscription',
      // One-time bottle count: null = follow the selected tier's quantity;
      // a number = the shopper set it manually with the stepper.
      onetimeQty: null
    };

    function onetimeQty() {
      if (state.onetimeQty) return state.onetimeQty;
      return bottlesFor(config.tiers[state.tierIndex]);
    }

    function monthsFor(tier) {
      return monthsForTier(tier, state.adults);
    }

    function bottlesFor(tier) {
      return Math.max(1, Math.ceil((monthsFor(tier) * state.adults) / config.months_per_bottle));
    }

    /**
     * Pick the selling plan for a tier: explicit override first, then a
     * non-bundle-group plan whose cadence matches the tier months, then the
     * longest-cadence non-bundle plan, then any plan at all.
     */
    function planFor(tier) {
      var allocations = config.allocations || [];
      if (tier.plan_id_override) {
        var override = null;
        allocations.forEach(function (a) {
          if (String(a.plan_id) === String(tier.plan_id_override)) override = a;
        });
        if (!override) {
          return { plan_id: tier.plan_id_override, plan_name: '', group_name: '', price: config.unit_price };
        }
        // Honor the override only while its cadence matches this tier's current
        // supply months — per-adult ladders change a tier's months, and a plan
        // pinned for one ladder (e.g. 6-month) is wrong for the other.
        var overrideMonths = planMonths(override.plan_name);
        if (overrideMonths === null || overrideMonths === monthsFor(tier)) return override;
      }
      var standard = allocations.filter(function (a) { return !a.is_bundle_group; });
      var pool = standard.length ? standard : allocations;
      var months = monthsFor(tier);
      var exact = pool.filter(function (a) { return planMonths(a.plan_name) === months; });
      if (exact.length) return exact[0];
      var sorted = pool.slice().sort(function (a, b) {
        return (planMonths(b.plan_name) || 0) - (planMonths(a.plan_name) || 0);
      });
      return sorted[0] || null;
    }

    function tierPricing(tier) {
      var qty = bottlesFor(tier);
      var months = monthsFor(tier);
      var full = config.unit_price * qty;
      var sheet = (config.pricing_by_bottles || {})[String(qty)];
      var plan = planFor(tier);
      var ongoingUnit = plan && plan.price ? plan.price : config.unit_price;
      var ongoing = ongoingUnit * qty;
      var total = sheet && sheet.first_total ? sheet.first_total : ongoing;
      return {
        qty: qty,
        months: months,
        full: full,
        total: total,
        ongoing: ongoing,
        code: sheet && sheet.first_total ? (sheet.code || '') : '',
        monthly: Math.round(total / months)
      };
    }

    function render() {
      tierEls.forEach(function (el, i) {
        var tier = config.tiers[i];
        if (!tier) return;
        var p = tierPricing(tier);
        var months = monthsFor(tier);
        var nameEl = el.querySelector('[data-dsb-tier-name]');
        var bottlesEl = el.querySelector('[data-dsb-tier-bottles]');
        var saveEl = el.querySelector('[data-dsb-tier-save]');
        var badgeEl = el.querySelector('[data-dsb-tier-badge]');
        var monthlyEl = el.querySelector('[data-dsb-tier-monthly]');
        var compareEl = el.querySelector('[data-dsb-tier-compare]');
        var totalEl = el.querySelector('[data-dsb-tier-total]');
        if (nameEl) nameEl.textContent = months + '-month supply';
        if (bottlesEl) {
          bottlesEl.hidden = !config.show_bottle_count;
          bottlesEl.textContent = p.qty + ' bottle' + (p.qty > 1 ? 's' : '') + ' · ';
        }
        if (saveEl) saveEl.textContent = tier.save_label || '';
        if (badgeEl) {
          badgeEl.textContent = config.highlight_badge || '';
          badgeEl.hidden = !(config.highlight_badge && months === config.highlight_months);
        }
        if (monthlyEl) monthlyEl.textContent = formatMoney(p.monthly);
        if (compareEl) compareEl.textContent = formatMoney(p.full);
        if (totalEl) totalEl.textContent = formatMoney(p.total);

        // Per-tier details (accordion content): cadence + renewal for THIS tier.
        var plan = planFor(tier);
        var deliveryMonths = plan ? planMonths(plan.plan_name) : null;
        var cadenceEl = el.querySelector('[data-dsb-cadence]');
        var cadenceMonthsEl = el.querySelector('[data-dsb-cadence-months]');
        var renewalEl = el.querySelector('[data-dsb-renewal]');
        var renewalTextEl = el.querySelector('[data-dsb-renewal-text]');
        if (cadenceEl && cadenceMonthsEl) {
          if (plan && deliveryMonths) {
            cadenceEl.style.display = '';
            cadenceMonthsEl.textContent = deliveryMonths + ' month' + (deliveryMonths > 1 ? 's' : '')
              + ' (' + p.qty + ' bottle' + (p.qty > 1 ? 's' : '') + ' per delivery)';
          } else {
            cadenceEl.style.display = 'none';
          }
        }
        if (renewalEl && renewalTextEl) {
          var showRenewal = p.total !== p.ongoing;
          renewalEl.style.display = showRenewal ? '' : 'none';
          if (showRenewal) {
            renewalTextEl.textContent = 'First order ' + formatMoney(p.total)
              + ', renews at ' + formatMoney(p.ongoing)
              + (deliveryMonths ? ' every ' + deliveryMonths + ' month' + (deliveryMonths > 1 ? 's' : '') : '');
          }
        }

        var selected = state.purchaseType === 'subscription' && state.tierIndex === i;
        el.classList.toggle('is-selected', selected);
        var input = el.querySelector('input');
        if (input) input.checked = selected;
      });

      adultsEls.forEach(function (el) {
        var selected = parseInt(el.getAttribute('data-dsb-adults'), 10) === state.adults;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      var activeTier = config.tiers[state.tierIndex];
      var activePricing = tierPricing(activeTier);

      // One-time row: quantity follows the selected tier until manually set.
      var otQty = onetimeQty();
      if (onetimePriceEl) {
        var onetimeTotal = Math.round(
          config.unit_price * otQty * (100 - (config.onetime_discount || 0)) / 100
        );
        onetimePriceEl.textContent = formatMoney(onetimeTotal);
      }
      if (otQtyEl) otQtyEl.textContent = otQty;
      if (otSubEl) otSubEl.textContent = otQty + ' bottle' + (otQty > 1 ? 's' : '');
      if (otMinusBtn) otMinusBtn.disabled = otQty <= 1;
      if (otPlusBtn) otPlusBtn.disabled = otQty >= (config.onetime_max_qty || 6);
      if (onetimeEl) {
        onetimeEl.classList.toggle('is-selected', state.purchaseType === 'onetime');
        if (onetimeInput) onetimeInput.checked = state.purchaseType === 'onetime';
      }

      if (atcBtn && !atcBtn.disabled) {
        var btnText = atcBtn.querySelector('.btn-text');
        if (btnText) {
          btnText.textContent = state.purchaseType === 'subscription'
            ? atcBtn.getAttribute('data-add-text')
            : atcBtn.getAttribute('data-onetime-text');
        }
      }
    }

    tierEls.forEach(function (el, i) {
      el.addEventListener('click', function () {
        state.tierIndex = i;
        state.purchaseType = 'subscription';
        render();
      });
    });

    adultsEls.forEach(function (el) {
      el.addEventListener('click', function () {
        var newAdults = parseInt(el.getAttribute('data-dsb-adults'), 10) || 1;
        if (newAdults === state.adults) return;
        // Keep the same supply length across ladders when possible; otherwise
        // fall back to the default-months tier, then the current index.
        var currentMonths = monthsForTier(config.tiers[state.tierIndex], state.adults);
        state.adults = newAdults;
        var keep = tierIndexByMonths(currentMonths, newAdults);
        if (keep < 0) keep = tierIndexByMonths(config.default_months, newAdults);
        if (keep >= 0) state.tierIndex = keep;
        render();
      });
    });

    if (onetimeEl) {
      onetimeEl.addEventListener('click', function () {
        state.purchaseType = 'onetime';
        render();
      });
    }

    function stepOnetimeQty(delta) {
      var max = config.onetime_max_qty || 6;
      var next = Math.min(max, Math.max(1, onetimeQty() + delta));
      state.onetimeQty = next;
      state.purchaseType = 'onetime';
      render();
    }
    if (otMinusBtn) {
      otMinusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        stepOnetimeQty(-1);
      });
    }
    if (otPlusBtn) {
      otPlusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        stepOnetimeQty(1);
      });
    }

    /**
     * Pre-apply the tier's discount code at checkout via Shopify's discount
     * URL (sets the cart-level discount cookie). Failure is non-fatal — the
     * shopper can still enter the code manually.
     */
    function applyDiscountCode(code) {
      if (!code || !config.auto_apply_codes) return Promise.resolve();
      return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
        .catch(function () { /* non-fatal */ });
    }

    if (atcBtn) {
      atcBtn.addEventListener('click', function () {
        if (atcBtn.disabled) return;
        var tier = config.tiers[state.tierIndex];
        var pricing = tierPricing(tier);
        var item = {
          id: config.variant_id,
          quantity: state.purchaseType === 'onetime' ? onetimeQty() : pricing.qty
        };
        var codePromise = Promise.resolve();
        if (state.purchaseType === 'subscription') {
          var plan = planFor(tier);
          if (plan && plan.plan_id) item.selling_plan = plan.plan_id;
          codePromise = applyDiscountCode(pricing.code);
        }

        var btnText = atcBtn.querySelector('.btn-text');
        var btnSpinner = atcBtn.querySelector('.btn-spinner');
        atcBtn.classList.add('loading');
        atcBtn.disabled = true;
        if (btnText) btnText.style.display = 'none';
        if (btnSpinner) btnSpinner.style.display = 'inline-flex';

        codePromise
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [item] })
            });
          })
          .then(function (response) { return response.json(); })
          .then(function (data) {
            if (data.status === 422 && item.selling_plan) {
              // Selling plan rejected (e.g. plan not on this variant) — retry one-time.
              delete item.selling_plan;
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [item] })
              }).then(function (r) { return r.json(); });
            }
            return data;
          })
          .then(function (data) {
            if (data.status === 422) {
              throw new Error(data.description || 'Product could not be added to cart');
            }
            if (typeof window.updateCartCount === 'function') window.updateCartCount();
            if (typeof window.openCartDrawer === 'function') {
              window.openCartDrawer(true);
            } else {
              document.dispatchEvent(new CustomEvent('cart:updated'));
            }
          })
          .catch(function (error) {
            alert('Error adding to cart: ' + error.message);
          })
          .finally(function () {
            atcBtn.classList.remove('loading');
            atcBtn.disabled = false;
            if (btnText) btnText.style.display = '';
            if (btnSpinner) btnSpinner.style.display = 'none';
            render();
          });
      });
    }

    render();
  }

  function init() {
    document.querySelectorAll('[data-dake-supply-bundles]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
